// Original source:
// - https://github.com/facebook/react/blob/0b974418c9a56f6c560298560265dcf4b65784bc/packages/react/src/ReactCache.js

// Based on code from Reselect (https://github.com/reduxjs/reselect)
// MIT License - Copyright (c) 2015-2024 Reselect Contributors

class StrongRef {
  constructor(value) {
    this.value = value;
  }
  deref() {
    return this.value;
  }
}

/**
 * @returns The StrongRef if WeakRef is not available.
 * @since 5.1.2
 * @internal
 */
const getWeakRef = () => (typeof WeakRef === "undefined" ? StrongRef : WeakRef);

const Ref = /* @__PURE__ */ getWeakRef();

const UNTERMINATED = 0;
const TERMINATED = 1;

function createCacheNode() {
  return {
    s: UNTERMINATED,
    v: undefined,
    o: null,
    p: null
  };
}

/**
 * Derefences the argument if it is a Ref. Else if it is a value already, return it.
 *
 * @param r - the object to maybe deref
 * @returns The derefenced value if the argument is a Ref, else the argument value itself.
 */
function maybeDeref(r) {
  if (r instanceof Ref) {
    return r.deref();
  }
  return r;
}

/**
 * Creates a tree of WeakMap-based cache nodes based on the identity of the
 * arguments it's been called with (in this case, the extracted values from your input selectors).
 * This allows weakMapMemoize to have an effectively infinite cache size.
 * Cache results will be kept in memory as long as references to the arguments still exist,
 * and then cleared out as the arguments are garbage-collected.
 *
 * @param {Function} func - The function to be memoized.
 * @param {object} [options={}]
 * @param {Function} [options.resultEqualityCheck] - If provided, used to compare a newly generated output value against previous values in the cache.
 * @returns A memoized function with a `.clearCache()` method attached.
 *
 * @since 5.0.0
 * @public
 * @experimental
 */
export function weakMapMemoizeCustom(func, options = {}) {
  let fnNode = createCacheNode();
  const { resultEqualityCheck } = options;

  let lastResult;

  let resultsCount = 0;

  function memoized() {
    let cacheNode = fnNode;
    const { length } = arguments;
    for (let i = 1, l = length; i < l; i++) {
      const arg = arguments[i];
      if (typeof arg === "function" || (typeof arg === "object" && arg !== null)) {
        // Objects go into a WeakMap
        let objectCache = cacheNode.o;
        if (objectCache === null) {
          cacheNode.o = objectCache = new WeakMap();
        }
        const objectNode = objectCache.get(arg);
        if (objectNode === undefined) {
          cacheNode = createCacheNode();
          objectCache.set(arg, cacheNode);
        } else {
          cacheNode = objectNode;
        }
      } else {
        // Primitives go into a regular Map
        let primitiveCache = cacheNode.p;
        if (primitiveCache === null) {
          cacheNode.p = primitiveCache = new Map();
        }
        const primitiveNode = primitiveCache.get(arg);
        if (primitiveNode === undefined) {
          cacheNode = createCacheNode();
          primitiveCache.set(arg, cacheNode);
        } else {
          cacheNode = primitiveNode;
        }
      }
    }

    const terminatedNode = cacheNode;

    let result;

    if (cacheNode.s === TERMINATED) {
      result = cacheNode.v;
    } else {
      // Allow errors to propagate
      result = func.apply(null, arguments);
      resultsCount++;

      if (resultEqualityCheck) {
        // Deref lastResult if it is a Ref
        const lastResultValue = maybeDeref(lastResult);

        if (lastResultValue != null && resultEqualityCheck(lastResultValue, result)) {
          result = lastResultValue;

          resultsCount !== 0 && resultsCount--;
        }

        const needsWeakRef = (typeof result === "object" && result !== null) || typeof result === "function";

        lastResult = needsWeakRef ? /* @__PURE__ */ new Ref(result) : result;
      }
    }

    terminatedNode.s = TERMINATED;

    terminatedNode.v = result;
    return result;
  }

  memoized.clearCache = () => {
    fnNode = createCacheNode();
    memoized.resetResultsCount();
  };

  memoized.resultsCount = () => resultsCount;

  memoized.resetResultsCount = () => {
    resultsCount = 0;
  };

  return memoized;
}
